import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useCopyToClipboard } from "react-use";

import {
  buildQrFileName,
  copyPngBlobToClipboard,
  downloadPngBlob,
} from "@/lib/qrImage";

/**
 * Flujo compartido de "generar imagen del QR con quiet zone → descargar →
 * copiar al portapapeles → avisar por toast", consumido por OrderPage y
 * ShareOrderModal. Mantener acá el manejo de toasts (en vez de en
 * src/lib/qrImage.js) permite testear la generación de la imagen sin
 * mockear la UI.
 */
export function useShareQrImage({ pedidoId, fallbackText }) {
  const shareQrRef = useRef(null);
  const isSharingRef = useRef(false);
  const [isSharing, setIsSharing] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();

  // Descarga + copia al portapapeles: comportamiento histórico de
  // "Copiar código", y también el fallback de `shareNativeOrFallback`
  // cuando el navegador no soporta compartir archivos vía Web Share API.
  const downloadAndCopy = async (blob) => {
    downloadPngBlob(blob, buildQrFileName(pedidoId));

    const copied = await copyPngBlobToClipboard(blob);
    if (copied) {
      toast.success(
        "El código QR ha sido guardado y copiado al portapapeles.",
      );
    } else {
      toast.success("El código QR ha sido guardado.");
      toast.error("La imagen no se pudo copiar al portapapeles.");
    }
  };

  const generateAndShare = async (visibleSvgElement) => {
    if (isSharingRef.current) return;

    if (!shareQrRef.current) {
      // Sin referencia al QR oculto ni al visible: solo copia el texto de
      // respaldo (mismo comportamiento que hoy cuando no hay ref).
      if (fallbackText) {
        copyToClipboard(fallbackText);
        toast.success("El ID de compra ha sido copiado al portapapeles.");
      }
      return;
    }

    isSharingRef.current = true;
    setIsSharing(true);

    try {
      const blob = await shareQrRef.current.toPngBlob(visibleSvgElement);
      await downloadAndCopy(blob);
    } catch (error) {
      console.error("Error al generar la imagen del QR:", error);
      toast.error("Error al generar la imagen.");
    } finally {
      isSharingRef.current = false;
      setIsSharing(false);
    }
  };

  /**
   * Comparte el QR real mediante el selector nativo del sistema operativo
   * (Web Share API con archivos adjuntos), igual que "Compartir QR" en
   * Sima-appmovil (expo-sharing abre el selector nativo, no una integración
   * directa con ninguna app). Nunca asume soporte por la sola existencia de
   * `navigator.share`: hace feature-detect explícito con
   * `navigator.canShare({ files })`, porque Chrome/Edge de escritorio en
   * particular exponen `navigator.share` pero rechazan compartir archivos.
   * Si no hay soporte, si `canShare` devuelve `false`, o si `navigator.share`
   * rechaza por cualquier motivo que no sea que el usuario canceló
   * (`AbortError`), cae al mismo flujo de descarga + portapapeles que
   * `generateAndShare` — el usuario nunca se queda sin ninguna forma de
   * obtener el QR.
   */
  const shareNativeOrFallback = async (visibleSvgElement) => {
    if (isSharingRef.current) return;

    if (!shareQrRef.current) {
      if (fallbackText) {
        copyToClipboard(fallbackText);
        toast.success("El ID de compra ha sido copiado al portapapeles.");
      }
      return;
    }

    isSharingRef.current = true;
    setIsSharing(true);

    try {
      const blob = await shareQrRef.current.toPngBlob(visibleSvgElement);
      const file = new File([blob], buildQrFileName(pedidoId), {
        type: "image/png",
      });
      const shareData = {
        files: [file],
        title: "Código QR de tu pedido",
        text: pedidoId
          ? `Código QR del pedido ${pedidoId}`
          : "Código QR de tu pedido",
      };

      let canShareFiles = false;
      try {
        canShareFiles =
          typeof navigator !== "undefined" &&
          typeof navigator.canShare === "function" &&
          typeof navigator.share === "function" &&
          navigator.canShare(shareData);
      } catch (canShareError) {
        // Algunos navegadores/contextos lanzan una excepción síncrona en vez
        // de devolver `false` (p. ej. bloqueado por política de permisos o
        // falta de gesto de usuario). Se trata igual que "sin soporte": cae
        // al fallback de descarga + portapapeles en vez de reportar un
        // fallo de generación de imagen que nunca ocurrió.
        console.error(
          "Error al verificar soporte de Web Share API:",
          canShareError,
        );
        canShareFiles = false;
      }

      if (canShareFiles) {
        try {
          await navigator.share(shareData);
          return;
        } catch (shareError) {
          if (shareError?.name === "AbortError") {
            // El usuario cerró el selector nativo sin elegir nada: no es un
            // error real, no se muestra ningún toast ni se cae al fallback.
            return;
          }
          console.error("Error al compartir el QR:", shareError);
          // Continúa hacia el fallback de descarga + portapapeles.
        }
      }

      await downloadAndCopy(blob);
    } catch (error) {
      console.error("Error al generar la imagen del QR:", error);
      toast.error("Error al generar la imagen.");
    } finally {
      isSharingRef.current = false;
      setIsSharing(false);
    }
  };

  return { shareQrRef, isSharing, generateAndShare, shareNativeOrFallback };
}
